import { FormEvent, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import type { MonitoredSite } from '../../types';

interface EditSiteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  site: MonitoredSite | null;
  onSubmit: (id: string, data: { name?: string; url?: string; expectedContent?: string; enabled?: boolean }) => Promise<void>;
}

export function EditSiteDialog({ open, onOpenChange, site, onSubmit }: EditSiteDialogProps) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [expectedContent, setExpectedContent] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (site) {
      setName(site.name);
      setUrl(site.url);
      setExpectedContent(site.expectedContent || '');
      setEnabled(site.enabled);
    }
  }, [site]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!site) return;

    setError('');

    // Validation
    if (!name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    if (!url.trim()) {
      setError('URL é obrigatória');
      return;
    }

    // URL must be https://
    if (!url.startsWith('https://') && !url.startsWith('http://')) {
      setError('URL deve começar com http:// ou https://');
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit(site.id, {
        name: name.trim(),
        url: url.trim(),
        expectedContent: expectedContent.trim() || undefined,
        enabled
      });

      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar site');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setError('');
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar Site</DialogTitle>
          <DialogDescription>
            Atualize as configurações do site monitorado.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded p-3">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="edit-name">Nome do Site</Label>
            <Input
              id="edit-name"
              placeholder="Ex: Site Principal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-url">URL</Label>
            <Input
              id="edit-url"
              type="url"
              placeholder="https://exemplo.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">
              Deve começar com http:// ou https://
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-expectedContent">Conteúdo Esperado (opcional)</Label>
            <Input
              id="edit-expectedContent"
              placeholder="Ex: Bem-vindo"
              value={expectedContent}
              onChange={(e) => setExpectedContent(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Texto que deve aparecer na página para considerar o site funcionando corretamente
            </p>
          </div>

          <div className="flex items-center justify-between space-x-2 border rounded-lg p-4">
            <div className="space-y-0.5">
              <Label htmlFor="edit-enabled">Monitoramento Ativo</Label>
              <p className="text-sm text-muted-foreground">
                Desabilite para pausar a verificação automática deste site
              </p>
            </div>
            <Switch
              id="edit-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
